<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="820px">
    <v-card class="profile-dialog-card entity-form-dialog">
      <div class="profile-dialog-header entity-form-dialog__header">
        <div class="entity-form-dialog__title">
          <v-avatar size="44" color="green lighten-5">
            <v-icon color="primary">mdi-account-group-outline</v-icon>
          </v-avatar>
          <div>
            <span>Parents</span>
            <h2>New parent</h2>
            <p>Create a household contact for students and billing.</p>
          </div>
        </div>
        <v-btn icon aria-label="Close new parent dialog" @click.stop="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <v-card-text class="profile-dialog-body entity-form-dialog__body">
        <section class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">mdi-account-multiple-outline</v-icon></span>
            <div>
              <h3>Parent information</h3>
              <p>Add the primary and secondary household contacts.</p>
            </div>
          </div>

          <v-row>
            <v-col cols="12" md="6">
              <div class="entity-form-dialog__subheading">Father / primary contact</div>
              <v-text-field
                v-model="customer.fatherName"
                outlined
                dense
                autofocus
                :error-messages="fatherNameErrors"
                :hide-details="fatherNameErrors.length === 0"
                label="Full name"
                @change="customer.fatherName = properCase(customer.fatherName)"
                @focus="$event.target.select()"
              />
              <v-text-field
                v-model="customer.fatherPhoneNumber"
                v-mask="'(###) ###-####'"
                outlined
                dense
                hide-details
                label="Phone number"
                @focus="$event.target.select()"
              />
              <v-text-field
                v-model="customer.fatherEmailAddress"
                outlined
                dense
                hide-details
                label="Email address"
              />
            </v-col>
            <v-col cols="12" md="6">
              <div class="entity-form-dialog__subheading">Mother / secondary contact</div>
              <v-text-field
                v-model="customer.motherName"
                outlined
                dense
                hide-details
                label="Full name"
                @change="customer.motherName = properCase(customer.motherName)"
                @focus="$event.target.select()"
              />
              <v-text-field
                v-model="customer.motherPhoneNumber"
                v-mask="'(###) ###-####'"
                outlined
                dense
                hide-details
                label="Phone number"
                @focus="$event.target.select()"
              />
              <v-text-field
                v-model="customer.motherEmailAddress"
                outlined
                dense
                hide-details
                label="Email address"
              />
            </v-col>
          </v-row>
        </section>

        <section class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">mdi-home-outline</v-icon></span>
            <div>
              <h3>Household address</h3>
              <p>Optional address used for family records.</p>
            </div>
          </div>

          <v-row>
            <v-col cols="12">
              <v-text-field v-model="customer.address" outlined dense hide-details label="Street address" />
            </v-col>
            <v-col cols="12" sm="5">
              <v-text-field
                v-model="customer.city"
                outlined
                dense
                hide-details
                label="City"
                @change="customer.city = properCase(customer.city)"
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model="customer.state"
                v-mask="'AA'"
                outlined
                dense
                hide-details
                label="State"
              />
            </v-col>
            <v-col cols="6" sm="4">
              <v-text-field
                v-model="customer.zip"
                v-mask="'#####'"
                outlined
                dense
                hide-details
                label="ZIP code"
              />
            </v-col>
          </v-row>
        </section>

        <section class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">mdi-phone-alert-outline</v-icon></span>
            <div>
              <h3>Emergency contact</h3>
              <p>Someone to contact when the parents are unavailable.</p>
            </div>
          </div>

          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="customer.emergencyContactName"
                outlined
                dense
                hide-details
                label="Full name"
                @change="customer.emergencyContactName = properCase(customer.emergencyContactName)"
                @focus="$event.target.select()"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="customer.emergencyContactNumber"
                v-mask="'(###) ###-####'"
                outlined
                dense
                hide-details
                label="Phone number"
                @focus="$event.target.select()"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="customer.emergencyContactEmailAddress"
                outlined
                dense
                hide-details
                label="Email address"
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="customer.note"
                outlined
                dense
                hide-details
                auto-grow
                rows="2"
                :label="$t('labels.note')"
              />
            </v-col>
          </v-row>
        </section>
      </v-card-text>

      <v-divider />

      <v-card-actions class="profile-dialog-actions entity-form-dialog__actions">
        <div class="entity-form-dialog__status">
          <span>Active</span>
          <toggle-button
            v-model="customer.isActive"
            :width="60"
            :labels="{ checked: 'Show', unchecked: 'Hide' }"
            :value="customer.isActive"
            :sync="true"
          />
        </div>
        <v-spacer />
        <v-btn text @click="close()">Cancel</v-btn>
        <v-btn color="primary" depressed :loading="loading" @click="submit()">
          <v-icon left small>mdi-account-plus-outline</v-icon>
          Create parent
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import customerService from "../service.js";
import GlobalFunction from "@/mixins/GlobalFunction";

const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");

export default {
  props: {
    createDialog: {
      default: false,
    },
    createFromStore: {
      type: Boolean,
    },
  },
  name: "create-customer",
  data() {
    return {
      foundError: false,
      loading: false,
      dialog: false,
      customer: {
        isActive: true,
        fatherName: null,
        fatherPhoneNumber: null,
        fatherEmailAddress: null,
        motherName: null,
        motherPhoneNumber: null,
        motherEmailAddress: null,
        emergencyContactName: null,
        emergencyContactNumber: null,
        emergencyContactEmailAddress: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        note: null,
      },
    };
  },
  mixins: [validationMixin, GlobalFunction],
  validations: {
    customer: {
      fatherName: {
        required,
      },
    },
  },
  computed: {
    ...mapGetters("global", ["currentUser"]),
    fatherNameErrors() {
      const errors = [];
      if (!this.$v.customer.fatherName.$dirty) return errors;
      if (!this.$v.customer.fatherName.required)
        errors.push(this.$t("validations.fieldIsRequired"));

      return errors;
    },
  },
  watch: {
    createDialog(val) {
      this.dialog = val;
    },
  },
  methods: {
    // properCase(string) {
    //   var sentence = string.toLowerCase().split(" ");
    //   for (var i = 0; i < sentence.length; i++) {
    //     sentence[i] = sentence[i][0].toUpperCase() + sentence[i].slice(1);
    //   }
    //   return sentence.join(" ");
    // },
    close() {
      this.empty();
      this.$emit("close");
    },
    empty() {
      this.customer = {
        isActive: true,
        fatherName: null,
        fatherPhoneNumber: null,
        fatherEmailAddress: null,
        motherName: null,
        motherPhoneNumber: null,
        motherEmailAddress: null,
        emergencyContactName: null,
        emergencyContactNumber: null,
        emergencyContactEmailAddress: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        note: null,
      };
      this.$v.$reset();
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        this.loading = true;
        if (this.customer.fatherName) {
          this.customer.name = this.customer.fatherName;
        } else {
          this.customer.name = this.customer.motherName;
        }
        if (this.customer.fatherPhoneNumber) {
          this.customer.phoneNumber = this.customer.fatherPhoneNumber;
        } else {
          this.customer.phoneNumber = this.customer.motherPhoneNumber;
        }
        this.customer.phoneNumber = this.customer.phoneNumber.replace(
          /[^a-zA-Z0-9]/g,
          ""
        );
        if (this.customer.fatherPhoneNumber) {
          this.customer.fatherPhoneNumber = this.customer.fatherPhoneNumber.replace(
            /[^a-zA-Z0-9]/g,
            ""
          );
        }
        if (this.customer.motherPhoneNumber) {
          this.customer.motherPhoneNumber = this.customer.motherPhoneNumber.replace(
            /[^a-zA-Z0-9]/g,
            ""
          );
        }
        return customerService
          .create(this.customer)
          .then((result) => {
            if (result.status) {
              this.$swal
                .mixin({
                  toast: true,
                  position: "top-end",
                  showConfirmButton: false,
                  timer: 3000,
                })
                .fire({
                  icon: "success",
                  title: "customer is created",
                });
              this.loading = false;
              this.$emit("submit");
              this.$events.emit("customerListEvent");
              this.close();
            }
          })
          .catch((err) => {
            console.log("err", err);
            this.loading = false;
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "error",
                title: err.data.message,
              });
          });
      }
    },
  },
};
</script>

<style scoped>
</style>
